/**
 * Frontend Image Generation using Sogni SDK
 *
 * Direct Sogni API calls from the browser when user is logged in.
 * Replaces backend proxy for image generation operations.
 */

import {
  isClientReady,
  getAccountSnapshot,
  getSogniClientInstance,
  getVideoModel,
  getModels,
  cancelImageProject,
  createProject,
  createVideoProject,
  registerProjectHandler,
  unregisterProjectHandler
} from '../sogni-client.svelte.js';
import { loadExactImageModels } from './image-model-selection.js';
import { createGalleryVideo } from './video-generation.js';
import { lookupProjectSensitive } from './session-recovery.js';
import { approveVideoProject } from './generation-billing.svelte.js';
import { assertRequestCurrent } from './generation-request.js';
import { getGenerationErrorDetails, copyGenerationErrorDetails, classifyGenerationFailure } from './generation-error-details.js';
import { isImageDeliveryUnverified } from './image-delivery-status.js';
import { readGenerationCapacity } from './generation-capacity.svelte.js';
import { createImageQueue } from './image-generation-queue.js';
import { createImageAttemptRequest } from './generation-attempt.js';

const DEFAULT_IMAGE_PROJECT_TIMEOUT_MS = 6 * 60 * 60 * 1000;
const DEFAULT_RENDER_STALL_TIMEOUT_MS = 30 * 60 * 1000;

function getPositiveMs(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

const IMAGE_PROJECT_TIMEOUT_MS = getPositiveMs(
  import.meta.env.VITE_IMAGE_PROJECT_TIMEOUT_MS,
  DEFAULT_IMAGE_PROJECT_TIMEOUT_MS
);
const RENDER_STALL_TIMEOUT_MS = getPositiveMs(
  import.meta.env.VITE_RENDER_STALL_TIMEOUT_MS,
  DEFAULT_RENDER_STALL_TIMEOUT_MS
);

const imageQueues = new Map();

function assertImageRequestCurrent(request) {
  const snapshot = getAccountSnapshot();
  assertRequestCurrent(request, {
    accountId: snapshot.authenticated ? snapshot.walletAddress?.toLowerCase() : null,
    sourceFingerprint: request.sourceFingerprint, subscription: snapshot.subscription
  });
}

function slotMetadata(slot, request, requestId) {
  return { slotId: slot.id, manifestId: request.id, requestId,
    batchIndex: slot.batchIndex, imageIndex: slot.imageIndex,
    batchId: `batch-${slot.batchIndex}`, imageId: `batch-${slot.batchIndex}-img-${slot.imageIndex}`,
    prompt: slot.prompt, model: slot.modelId, requestedModel: slot.modelId,
    seed: slot.projectConfig.seed, width: slot.projectConfig.width, height: slot.projectConfig.height,
    projectConfig: slot.projectConfig };
}

/** Stop the active admission loop, then request cancellation of its accepted
 * projects. Saved uncertain projects can also be canceled by their exact ID. */
export async function cancelImageGeneration(request, results = {}, onProgress) {
  const active = imageQueues.get(request.id);
  const accepted = active ? await active.cancel() : null;
  const counts = { confirmed: accepted?.confirmed || 0,
    unconfirmed: accepted?.unconfirmed || 0, finished: accepted?.finished || 0 };
  const attempted = new Set(accepted?.attemptedProjectIds);
  const unresolved = new Set(['pending', 'submitting', 'queued', 'rendering', 'preview', 'unverified', 'pending-delivery', 'recovering']);
  for (const slot of request.slots) {
    const result = results[slot.id];
    if (!result?.projectId || attempted.has(result.projectId)
      || (result.unverified !== true && !unresolved.has(result.status))) continue;
    attempted.add(result.projectId);
    const cancellation = await cancelImageProject(result.projectId, request);
    if (cancellation.confirmed === true) {
      counts.confirmed += 1;
      onProgress?.({ ...slotMetadata(slot, request, result.requestId ?? null),
        projectId: result.projectId, type: 'generate:image-canceled', status: 'canceled',
        unverified: false, timestamp: Date.now() });
    } else if (cancellation.state === 'finished') counts.finished += 1;
    else counts.unconfirmed += 1;
  }
  return counts;
}

/**
 * Generate images using frontend Sogni SDK
 *
 * @param {object} request - Reviewed immutable image slots
 * @param {function} onProgress - Progress callback
 * @returns {Promise<object>} - Generation result
 */
export async function generateImagesDirect(request, onProgress) {
  if (!isClientReady()) throw new Error('Sogni client not connected. Please sign in again.');
  const requestId = crypto.randomUUID(); // A new attempt; manifest/slot IDs stay stable.
  request = createImageAttemptRequest(request, requestId);
  const startedAt = Date.now();
  const queue = createImageQueue({ request,
    readCapacity: () => readGenerationCapacity(request),
    readBalance: () => getAccountSnapshot().balance,
    assertCurrent: () => assertImageRequestCurrent(request),
    startSlot: slot => processSlot(slot, request, requestId, onProgress),
    cancelProject: (projectId) => cancelImageProject(projectId, request),
    shouldPauseOnOutcome: outcome => ['auth', 'queue', 'balance', 'spark-tier', 'entitlement',
      'billing-recovery', 'plan-limit', 'trial-limit', 'fair-use', 'payment-verification', 'free-limits', 'request-cost']
      .includes(classifyGenerationFailure(outcome)),
    onUpdate: state => onProgress?.({ type: 'generate:queue-updated', requestId,
      manifestId: request.id, queue: state, timestamp: Date.now() }),
    onPause: (slot, reason) => onProgress?.({ ...slotMetadata(slot, request, requestId),
      type: 'generate:image-paused', projectId: null, unverified: false,
      error: reason.message, errorCode: reason.code, timestamp: Date.now() }),
    onCancelConfirmed: (slot, result) => onProgress?.({ ...slotMetadata(slot, request, requestId),
      projectId: result.projectId, type: 'generate:image-canceled', status: 'canceled',
      unverified: false, timestamp: Date.now() }),
    onCancelFailure: (slot, result) => onProgress?.({ ...slotMetadata(slot, request, requestId),
      type: 'generate:cancel-unconfirmed', message: result.message || result.error?.message || 'Cancellation was not confirmed. This image remains active.',
      timestamp: Date.now() })
  });
  imageQueues.set(request.id, queue);
  try {
    try {
      assertImageRequestCurrent(request);
      // Stop owns preparation while this exact attempt is saved before paid create.
      await onProgress?.({ type: 'request:started', requestId, manifestId: request.id, request });
      onProgress?.({ type: 'generate:started', requestId, totalBatches: request.totalBatches,
        imagesPerBatch: request.imagesPerPrompt, modelsUsed: request.modelIds, timestamp: Date.now() });
      for (const network of new Set(request.slots.map(slot => slot.projectConfig.network))) {
        if (queue.stopped) break;
        await loadExactImageModels({ loadModels: () => getModels(network),
          requestedModelIds: [...new Set(request.slots.filter(slot => slot.projectConfig.network === network).map(slot => slot.modelId))] });
      }
      if (!queue.stopped) assertImageRequestCurrent(request);
    } catch (error) {
      for (const slot of request.slots) onProgress?.({ ...slotMetadata(slot, request, requestId),
        type: 'generate:image-paused', projectId: null, unverified: false,
        error: error.message, errorCode: error.code, timestamp: Date.now() });
      onProgress?.({ type: 'generate:queue-updated', requestId, manifestId: request.id,
        queue: { status: 'paused', pending: 0, submitted: 0, active: 0, paused: request.slots.length,
          completed: 0, capacity: null, blocked: { code: error.code, message: error.message } }, timestamp: Date.now() });
      throw error;
    }
    const result = await queue.run();
    const totalTime = Date.now() - startedAt;
    onProgress?.({ type: 'generate:completed', requestId, totalBatches: request.totalBatches,
      status: result.status, totalTime, modelsUsed: request.modelIds, timestamp: Date.now() });
    return { success: result.status === 'completed', status: result.status, request, attemptId: requestId,
      totalBatches: request.totalBatches, totalTime };
  } catch (error) {
    error.request = request;
    onProgress?.({ type: 'error', requestId, error: error.message, errorCode: error.code, request,
      status: error.code === 'SESSION_SAVE_FAILED' ? 'paused' : 'error' });
    throw error;
  } finally {
    imageQueues.delete(request.id);
  }
}

/**
 * Generate an image-to-video project through the signed-in user's browser
 * client. Keeping this path direct means the render is billed to the active
 * Sogni account and avoids uploading a persisted Gallery Blob through Vibe's
 * JSON API.
 *
 * Model-specific duration, FPS, and dimension validation happens in the shared
 * video config before this helper is called. The SDK receives `duration`
 * instead of a hand-calculated frame count so it can apply the correct LTX or
 * WAN frame rules.
 *
 * @param {Blob} referenceImage - Favorite image stored in IndexedDB
 * @param {object} options - Validated video project settings
 * @returns {Promise<object>} Project result plus the first generated video URL
 */
export async function generateVideoDirect(referenceImage, options = {}) {
  const {
    modelId,
    prompt,
    width,
    height,
    fps,
    durationSeconds,
    modelParameters,
    generateAudio,
    network,
    ratio,
    billingMode,
    accountId = getAccountSnapshot().walletAddress?.toLowerCase(),
    seed,
    attribution,
    beforeSubmit,
    onSubmitted,
    onSubmissionFailure,
    runControl,
    timeout
  } = options;

  if (!isClientReady()) {
    throw new Error('Sogni client not connected. Please sign in again.');
  }
  if (!(referenceImage instanceof Blob)) {
    throw new Error('The selected Gallery image is unavailable. Please save it again.');
  }
  if (!modelId || typeof modelId !== 'string') {
    throw new Error('A video model is required.');
  }
  if (!prompt || !prompt.trim()) {
    throw new Error('Describe how the image should move.');
  }

  return createGalleryVideo(referenceImage, {
    prompt,
    modelId,
    width,
    height,
    fps,
    durationSeconds,
    modelParameters,
    generateAudio,
    network,
    ratio,
    billingMode,
    accountId,
    seed,
    attribution,
    beforeSubmit,
    onSubmitted,
    onSubmissionFailure,
    runControl,
    timeout
  }, { getVideoModel, createVideoProject, approveProject: approveVideoProject,
    getAccountId: () => getAccountSnapshot().walletAddress?.toLowerCase(),
    // A completion the SDK replayed after a reconnect carries no label; one raw read restores it.
    lookupSensitive: projectId => lookupProjectSensitive(getSogniClientInstance().projects, projectId) });
}

/**
 * Submit one saved slot and normalize its event-driven terminal outcome.
 */
function processSlot(slot, request, requestId, onProgress) {
  const operation = createProjectWithProgress(
    slot.projectConfig, slot.batchIndex, slot.imageIndex, slot.prompt, slot.modelId,
    slot.modelId, requestId, onProgress, slot.id, request
  );
  const metadata = slotMetadata(slot, request, requestId);
  return { submitted: operation.submitted, completed: operation.completed.then(
    result => ({ ...metadata, ...result.renderDetails,
      url: result.imageUrls?.[0] || null, status: result.canceled ? 'canceled' : 'done', completedAt: Date.now() }),
    error => {
      const details = getGenerationErrorDetails(error);
      const code = details.code;
      const failure = { ...metadata, url: null, status: 'error', error: details.message || error.message,
        errorCode: code, errorDetails: details, cause: error,
        projectId: details.projectId || null, unverified: isImageDeliveryUnverified(error),
        isNSFWFiltered: error?.isNSFWFiltered === true, isPremiumError: Number(code) === 4054,
        originalCode: details.originalCode || null,
        isRetiredModel: Number(code) === 4053 };
      onProgress?.({ ...failure, type: 'generate:image-failed', timestamp: Date.now() });
      return failure;
    }
  ) };
}

/**
 * Create a project with per-image progress reporting
 * Uses waitForCompletion: false and tracks completion via events
 * to work around SDK waitForCompletion timeout issues
 */
function createProjectWithProgress(
  config,
  batchIndex,
  imageIndex,
  prompt,
  model,
  requestedModel,
  requestId,
  onProgress,
  slotId,
  request
) {
  const manifestId = request.id;
  const requestedTimeout = getPositiveMs(config.timeout, 0);
  const timeout = Math.max(requestedTimeout, IMAGE_PROJECT_TIMEOUT_MS);

  // Once a project reports render progress it is alive on a worker, so the deadline keeps
  // sliding: each progress event re-arms the timer. The default timeout is intentionally
  // long so queued batch renders can be left running while the user steps away.
  const PROGRESS_STALL_TIMEOUT = RENDER_STALL_TIMEOUT_MS;

  // Use waitForCompletion: false and handle completion ourselves
  const projectConfig = {
    ...config,
    waitForCompletion: false
  };

  let resolveSubmission;
  let rejectSubmission;
  const submitted = new Promise((resolve, reject) => { resolveSubmission = resolve; rejectSubmission = reject; });
  const completed = new Promise((resolve, reject) => {
    let resolved = false;
    let projectId = null;
    let project = null;
    let sawRenderProgress = false;
    const startTime = Date.now();
    const initialDeadline = startTime + timeout;

    const cleanup = () => {
      if (projectId) {
        unregisterProjectHandler(projectId);
      }
    };

    let timeoutId = null;
    const armTimeout = (ms) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          cleanup();
          const elapsed = Date.now() - startTime;
          const error = new Error(sawRenderProgress
            ? `Project timed out after ${elapsed}ms (render stalled)`
            : `Project timed out after ${elapsed}ms`);
          error.code = 'PROJECT_RESULT_UNVERIFIED';
          error.projectId = projectId;
          rejectSubmission(error);
          reject(error);
        }
      }, ms);
    };
    armTimeout(timeout);

    // Handler function that will be called when events arrive
    const handleEvent = (event) => {
      if (resolved) return;

      if (event.type === 'project-status') {
        onProgress?.({ type: 'generate:image-status', ...slotMetadata({ id: slotId, batchIndex, imageIndex,
          prompt, modelId: model, projectConfig: config }, request, requestId), projectId,
          projectStatus: event.status, queuePosition: event.queuePosition, waitingReason: event.waitingReason,
          estimatedStartAt: event.estimatedStartAt, progress: event.percentage,
          timestamp: Date.now() });
        return;
      }

      if (event.type === 'image-canceled') {
        resolved = true;
        clearTimeout(timeoutId);
        cleanup();
        const renderDetails = { ...slotMetadata({ id: slotId, batchIndex, imageIndex, prompt,
          modelId: model, projectConfig: config }, request, requestId), projectId, unverified: false };
        onProgress?.({ ...renderDetails, type: 'generate:image-canceled', timestamp: Date.now() });
        resolve({ project, renderDetails, canceled: true });
        return;
      }

      if (event.type === 'progress') {
        // Only extend for real render activity (queued projects can report 0%). Never
        // shorten the initial window — a render may report early then pause briefly.
        if (event.percentage > 0) {
          if (!sawRenderProgress) {
            sawRenderProgress = true;
            console.log(`   🏃 Project ${projectId} rendering (deadline now slides with progress)`);
          }
          armTimeout(Math.max(initialDeadline - Date.now(), PROGRESS_STALL_TIMEOUT));
        }
        if (event.percentage > 0) onProgress?.({ type: 'generate:image-status', requestId,
          manifestId, slotId, projectId, batchIndex, imageIndex, projectConfig: config,
          projectStatus: 'processing', progress: event.percentage, timestamp: Date.now() });
        return;
      }

      if (event.type === 'image-preview') {
        // A preview frame means the worker is actively rendering — slide the deadline.
        if (event.percentage > 0) {
          if (!sawRenderProgress) sawRenderProgress = true;
          armTimeout(Math.max(initialDeadline - Date.now(), PROGRESS_STALL_TIMEOUT));
        }
        if (onProgress && event.previewUrl) {
          onProgress({
            type: 'generate:image-preview',
            requestId,
            slotId,
            manifestId,
            projectId,
            projectConfig: config,
            batchIndex,
            imageIndex,
            previewUrl: event.previewUrl,
            progress: event.percentage,
            step: event.step,
            stepCount: event.stepCount,
            imageId: `batch-${batchIndex}-img-${imageIndex}`,
            timestamp: Date.now()
          });
        }
        return;
      }

      console.log(`   📥 Event received for project ${projectId}:`, event.type, event.imageUrl ? '(has URL)' : '');

      if (event.type === 'image-completed' && event.imageUrl) {
        resolved = true;
        clearTimeout(timeoutId);
        cleanup();

        const renderDetails = {
          requestId,
          slotId,
          manifestId,
          projectConfig: config,
          requestedModel: requestedModel || model,
          projectId: event.projectId || projectId,
          jobId: event.jobId || null,
          workerName: event.workerName || null,
          // Worker-reported seed wins; fall back to the seed we requested so
          // completed images never surface "Not recorded".
          seed: event.seed ?? config.seed ?? null,
          width: config.width ?? null,
          height: config.height ?? null,
          steps: config.steps ?? null,
          guidance: config.guidance ?? null,
          sampler: config.sampler ?? null,
          scheduler: config.scheduler ?? null,
          // Server label on a delivered result; the UI covers it per the viewer's switches.
          sensitive: event.sensitive === true
        };

        // Send progress event
        if (onProgress) {
          onProgress({
            type: 'generate:image-completed',
            requestId,
            batchIndex,
            imageIndex,
            imageUrl: event.imageUrl,
            imageId: `batch-${batchIndex}-img-${imageIndex}`,
            prompt,
            model,
            status: 'done',
            ...renderDetails,
            timestamp: Date.now()
          });
        }

        resolve({
          project,
          imageUrls: [event.imageUrl],
          renderDetails,
          completed: true
        });
      } else if (event.type === 'image-failed') {
        resolved = true;
        clearTimeout(timeoutId);
        cleanup();
        const err = new Error(event.error || 'Image generation failed');
        copyGenerationErrorDetails(err, event);
        err.projectId = projectId;
        err.unverified = isImageDeliveryUnverified(err, projectId);
        if (event.isNSFWFiltered) err.isNSFWFiltered = true;
        reject(err);
      }
    };

    const submit = async () => {
      onProgress?.({ type: 'generate:image-submitting', requestId, manifestId, slotId,
        batchIndex, imageIndex, projectConfig: config, prompt, model, seed: config.seed,
        projectId: null, unverified: false,
        timestamp: Date.now() });
      // Create the project
      const result = await createProject(projectConfig, { request });

      if (!result.project || !result.project.id) {
        clearTimeout(timeoutId);
        throw new Error('No project ID returned');
      }

      projectId = result.project.id;
      project = result.project;
      resolveSubmission({ projectId, project });

      onProgress?.({ type: 'generate:image-created', requestId, manifestId, slotId,
        batchIndex, imageIndex, projectId, projectConfig: config, prompt, model,
        projectStatus: project.status, queuePosition: project.queuePosition,
        estimatedStartAt: project.estimatedStartAt, waitingReason: project.waitingReason,
        seed: config.seed, unverified: resolved, timestamp: Date.now() });

      console.log(`   🆔 Project created: ${projectId}`);

      // If the timer fired while createProject was in flight, don't register a handler
      // that nothing will ever unregister.
      if (resolved) {
        return;
      }

      // Register handler NOW - events might already be queued
      registerProjectHandler(projectId, handleEvent);
    };
    submit().catch(error => {
      resolved = true;
      clearTimeout(timeoutId);
      cleanup();
      rejectSubmission(error);
      reject(error);
    });
  });
  return { submitted, completed };
}
